import type { Invitation } from '@/types'

import {
    Cake,
    GlassWater,
    MoonStar,
    Music,
    UtensilsCrossed
} from 'lucide-react'

const HONOREE = "Camille"
const AGE = 30
const RSVP_DEADLINE = "samedi 28 novembre 2026"

// Single source of truth for every detail shown on the invitation.
// startsAt and endsAt drive the countdown and the calendar files: keep them in sync with the date and time labels.
export const invitation: Invitation = {
    honoree: HONOREE,
    age: AGE,
    intro: "Une grande soirée pour célébrer trois décennies de fous rires, de voyages et de belles rencontres. Et la fête ne serait vraiment pas la même sans vous.",
    startsAt: '2026-12-12T19:30:00+01:00',
    endsAt: '2026-12-13T02:00:00+01:00',
    date: {
        full: "Samedi 12 décembre 2026",
        weekdayShort: "Sam.",
        day: "12",
        monthShort: "Déc.",
        year: "2026"
    },
    time: {
        start: "19h30",
        end: "2h00"
    },
    rsvpDeadline: RSVP_DEADLINE,
    maxPartySize: 6,
    venue: {
        name: "Le Comptoir des Lilas",
        street: "12 rue des Lilas",
        postalCode: "75019",
        city: "Paris",
        area: "Paris 19e",
        description: "Une salle chaleureuse privatisée pour l’occasion, avec un bar, un coin salon et une vraie piste de danse."
    },
    access: {
        title: "Métro Place des Fêtes",
        text: "Lignes 7 bis et 11, à 3 minutes à pied. Stationnement payant dans les rues voisines, mais le métro reste le plus simple."
    },
    dressCode: {
        title: "Chic et coloré",
        text: "Sortez vos plus belles tenues et osez la couleur : une touche de rose, de jaune ou de paillettes est vivement encouragée."
    },
    gifts: {
        title: "Votre présence suffit",
        text: "Le plus beau des cadeaux, c’est de vous avoir à mes côtés. Si vous tenez vraiment à me gâter, une urne sera prévue sur place pour mon prochain voyage au Japon."
    },
    message: {
        title: "Trente ans, ça se fête !",
        paragraphs: [
            "Trois décennies de fous rires, de voyages, de rencontres et de souvenirs, et vous faites tous partie de l’histoire. Pour marquer le coup, j’ai envie de réunir les personnes qui comptent le plus pour moi, le temps d’une grande soirée.",
            "Au programme : de bons petits plats, des bulles, beaucoup de musique et, je l’espère, une piste de danse qui ne désemplit pas. Il ne manque plus que vous !"
        ],
        signature: HONOREE
    },
    program: [
        {
            time: "19h30",
            title: "Accueil et cocktails",
            description: "Bulles, cocktails maison et petites bouchées pour se retrouver en douceur.",
            icon: GlassWater,
            color: 'saffron'
        },
        {
            time: "20h30",
            title: "Grand buffet",
            description: "Un buffet gourmand et généreux, avec de belles options végétariennes.",
            icon: UtensilsCrossed,
            color: 'teal'
        },
        {
            time: "22h00",
            title: "Gâteau et bougies",
            description: "Chansons, souvenirs partagés et peut-être une ou deux surprises.",
            icon: Cake,
            color: 'raspberry'
        },
        {
            time: "22h30",
            title: "Piste de danse",
            description: "Un DJ aux platines, avec les chansons demandées par les invités.",
            icon: Music,
            color: 'lilac'
        },
        {
            time: "2h00",
            title: "Dernière danse",
            description: "On se dit au revoir… et on se donne rendez-vous pour la prochaine !",
            icon: MoonStar,
            color: 'coral'
        }
    ],
    faq: [
        {
            question: "Puis-je venir accompagné·e ?",
            answer: "Bien sûr ! Indiquez simplement le nombre de personnes dans le formulaire de réponse, pour que je prévoie assez de place (et de gâteau)."
        },
        {
            question: "Les enfants sont-ils les bienvenus ?",
            answer: "La soirée est plutôt pensée pour les grands. Si vous ne trouvez pas de solution de garde, écrivez-le-moi dans le formulaire et on s’organisera ensemble."
        },
        {
            question: "Jusqu’à quand puis-je répondre ?",
            answer: `Merci de répondre avant le ${RSVP_DEADLINE}, même si vous ne pouvez pas venir. Cela m’aide à confirmer le nombre d’invités avec le traiteur.`
        },
        {
            question: "Puis-je modifier ma réponse ?",
            answer: "Oui, à tout moment. Renvoyez simplement le formulaire avec la même adresse e-mail : votre nouvelle réponse remplacera la précédente."
        },
        {
            question: "Et si j’ai une allergie ou un régime particulier ?",
            answer: "Précisez-le dans le formulaire de réponse. Le buffet proposera des plats végétariens, et je transmettrai toutes les allergies au traiteur."
        },
        {
            question: "Comment rentrer après la soirée ?",
            answer: "Le samedi soir, le métro circule plus tard que d’habitude, jusque vers 2h du matin. Les taxis et VTC sont aussi faciles à trouver dans le quartier."
        }
    ]
}
